import { useEffect, useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { getConsent, setConsent, CONSENT_EVENT } from "@/components/CookieConsent";
import { MapPin, Phone, Mail, Clock, Navigation, Waves } from "lucide-react";

const HOURS = [
  ["Monday – Sunday", "8:00 – 17:00"],
];

const NEARBY = ["Viking Bay", "Bleak House", "Dickens House Museum", "Botany Bay", "Stone Bay"];

const Visit = () => {
  const [consent, setConsentState] = useState(getConsent());

  useEffect(() => {
    const onChange = () => setConsentState(getConsent());
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  return (
  <section id="visit" data-testid="visit-section" className="py-28 md:py-36 relative overflow-hidden">
    <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-mint/60 blur-3xl" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <SectionHeading chapter="07" overline="Visit Us" title="Find Us in Beautiful Broadstairs" />

      <div className="mt-16 grid lg:grid-cols-12 gap-10 items-stretch">
        <div className="lg:col-span-5 flex flex-col gap-8">
          <Reveal>
            <div className="bg-white rounded-[2rem] p-8 shadow-lg shadow-teal/10 border border-gold/20">
              <h3 className="flex items-center gap-3 font-display font-bold text-2xl"><MapPin className="text-gold" size={22} /> The Parlour</h3>
              <p className="mt-4 text-teal/80 leading-relaxed">Morelli&rsquo;s Ice Cream Parlour<br />14 Victoria Parade<br />Broadstairs, Kent CT10 1QS</p>
              <div className="mt-5 flex flex-col gap-2 text-sm text-teal/80">
                <a href="tel:+441843862500" data-testid="visit-phone" className="flex items-center gap-3 hover:text-gold transition-colors duration-300"><Phone size={16} className="text-gold" /> 01843 862500</a>
                <a href="mailto:info@morellis.com" data-testid="visit-email" className="flex items-center gap-3 hover:text-gold transition-colors duration-300"><Mail size={16} className="text-gold" /> info@morellis.com</a>
              </div>
              <a href="https://www.google.com/maps/dir/?api=1&destination=Morelli's+Ice+Cream+Parlour+Broadstairs" target="_blank" rel="noopener noreferrer"
                data-testid="visit-directions-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-teal text-cream px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-gold transition-colors duration-300 active:scale-95">
                <Navigation size={14} /> Get Directions
              </a>
              <a href="#dogs" data-testid="visit-dogs-badge"
                className="mt-4 inline-flex items-center gap-3 rounded-full bg-mint/70 border border-gold/40 px-5 py-2.5 hover:bg-mint transition-colors duration-300">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B2A86" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" transform="translate(-4.5 0)" />
                  <path d="M11 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" transform="translate(4.5 0)" />
                  <path d="M11 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" transform="translate(-7.5 4)" />
                  <path d="M11 6.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" transform="translate(7.5 4)" />
                  <path d="M12 12c-2.8 0-5 2-5 4.4 0 1.6 1.2 2.6 2.7 2.6 1 0 1.6-.4 2.3-.4s1.3.4 2.3.4c1.5 0 2.7-1 2.7-2.6 0-2.4-2.2-4.4-5-4.4z" />
                </svg>
                <span className="text-xs uppercase tracking-[0.15em] text-teal font-medium">Dogs welcome — dog gelato £1</span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-mint/60 rounded-[2rem] p-8 border border-gold/20">
              <h3 className="flex items-center gap-3 font-display font-bold text-2xl"><Clock className="text-gold" size={22} /> Opening Hours</h3>
              <dl className="mt-4 flex flex-col gap-3">
                {HOURS.map(([day, time]) => (
                  <div key={day} className="flex items-baseline justify-between gap-4 border-b border-teal/10 pb-2">
                    <dt className="text-sm text-teal/70">{day}</dt>
                    <dd className="font-display font-semibold text-teal">{time}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-xs text-teal/60 italic">Hours may vary on bank holidays and during winter months.</p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div>
              <h3 className="flex items-center gap-3 font-display font-bold text-xl"><Waves className="text-gold" size={20} /> While You&rsquo;re Here</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {NEARBY.map((n) => (
                  <span key={n} className="rounded-full border border-teal/25 px-4 py-1.5 text-xs uppercase tracking-[0.15em] text-teal/70">{n}</span>
                ))}
              </div>
              <figure className="mt-6 relative overflow-hidden rounded-[1.75rem] border-4 border-white shadow-xl shadow-teal/15" data-testid="visit-beach-photo">
                <img src="/viking-bay.webp" alt="A bustling summer day on Viking Bay beach, moments from the parlour" loading="lazy"
                  className="w-full h-56 object-cover hover:scale-105 transition-transform duration-700" />
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-teal/70 to-transparent px-5 pb-3 pt-8">
                  <span className="font-display italic text-cream text-sm">Viking Bay — two minutes&rsquo; stroll away</span>
                </figcaption>
              </figure>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="h-full min-h-[26rem] rounded-t-[10rem] rounded-b-[2rem] overflow-hidden border-4 border-gold/60 shadow-2xl shadow-teal/20 relative">
            {consent === "accepted" ? (
              <iframe title="Map showing Morelli's Ice Cream Parlour, 14 Victoria Parade, Broadstairs CT10 1QS"
                data-testid="visit-map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=1.4326%2C51.3520%2C1.4546%2C51.3646&layer=mapnik&marker=51.35830%2C1.44360"
                className="absolute inset-0 w-full h-full" loading="lazy" />
            ) : (
              <div className="absolute inset-0 bg-mint/50 flex flex-col items-center justify-center text-center px-8" data-testid="visit-map-consent">
                <p className="font-display italic text-2xl text-teal">Find us on the seafront</p>
                <p className="mt-3 text-sm text-teal/70 max-w-sm leading-relaxed">
                  14 Victoria Parade, Broadstairs CT10 1QS — two minutes from Viking Bay.
                  The interactive map is provided by OpenStreetMap and needs your cookie consent to load.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <button onClick={() => setConsent("accepted")} data-testid="visit-map-accept"
                    className="rounded-full bg-teal text-cream text-xs uppercase tracking-[0.2em] font-semibold px-6 py-3 hover:bg-gold hover:text-teal-deep transition-colors duration-300 active:scale-95">
                    Accept &amp; show map
                  </button>
                  <a href="https://www.openstreetmap.org/?mlat=51.35830&mlon=1.44360#map=17/51.35830/1.44360" target="_blank" rel="noopener noreferrer"
                    data-testid="visit-map-external"
                    className="rounded-full border border-teal/30 text-teal text-xs uppercase tracking-[0.2em] px-6 py-3 hover:border-gold hover:text-gold transition-colors duration-300">
                    Open map without cookies
                  </a>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

export default Visit;
